/**
 * React hook for type-safe event tracking.
 *
 * Usage:
 *   const { track, isFeatureEnabled } = useAnalytics();
 *   track('CHECKUP_COMPLETED', { journey_id: '...', ... });
 *
 * See Part 22 §128.1 of the Tech Guidelines.
 */

import { useCallback } from 'react';
import { useObservabilityClients } from '../../context/context-provider';
import type { EventName, EventProperties } from '../analytics-schema';

export function useAnalytics() {
  const { analytics } = useObservabilityClients();

  const track = useCallback(
    <E extends EventName>(event: E, properties: EventProperties<E>) => {
      analytics.track(event, properties);
    },
    [analytics]
  );

  const isFeatureEnabled = useCallback(
    (flag: string): boolean => {
      return analytics.isFeatureEnabled(flag);
    },
    [analytics]
  );

  const getFeatureFlag = useCallback(
    (flag: string): string | boolean | undefined => {
      return analytics.getFeatureFlag(flag);
    },
    [analytics]
  );

  return { track, isFeatureEnabled, getFeatureFlag };
}
